import React, { useContext, useState, useRef, useMemo, useCallback } from 'react'
import _ from 'lodash'
import styled from 'styled-components'
import BuffetContainer from 'components/buffet/BuffetContainer'
import { useNavigate } from 'react-router-dom'
import BtnBack from 'components/base-ui/BtnBack'
import colors from 'styles/colors'
import LabelWithIcon from 'components/base-ui/LabelWithIcon'
import PrivateCartItemList from 'components/dine-in/PrivateCartItemList'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import {
  buffetSelector,
  openModalEditProductModifier,
  onCurrentPackageChange,
  setLoadingModifier,
  setProductSelected,
  setBuffetEndTime,
} from 'store/slices/buffetSlice'
import {
  clearCurrentPrivateCart,
  privateCartSelector,
  removePrivateCartItem,
  setPrivateCartSelectedIndex,
  updatePrivateCartItem,
} from 'store/slices/privateCartSlice'
import { useAppDispatch } from 'store'
import { Button, Fonts, Modal, ScrollPage, LabelInfo, ContentResize } from 'components/base-ui'
import TakeAwayLineItemsContainer from 'components/dine-in/TakeAwayLineItemsContainer'
import {
  useBuffetOrderPackageQuery,
  useSubmitOrderLineItemsMutation,
  OrderInvalidEnum,
  OrderLineItemInput,
  LineItemServiceEnum,
} from 'graphQL/dinein/generate/operations'
import { dineInClient } from 'variables/services'
import { AppContext } from 'contexts/AppProvider'
import ModalConfirm from 'components/base-ui/ModalConfirm'
import EmptyLineItems from 'pages/dine-in/pages/EmptyLineItems'
import ModalConfirmSubmitLineItem from 'components/dine-in/modal/ModalConfirmSubmitLineItem'
import { WatchQueryFetchPolicy } from '@apollo/client'
import { getOrderInvalidWarningText } from 'helpers/payment'
import { generateSrcSet } from 'helpers/image'
import { SubPageName } from 'enums/buffet'
import useValidateCurrentOrder, {
  OrderInvalidEnum as BuffetOrderInvalidEnum,
} from 'hooks/buffet/useValidateCurrentOrder'
import useModal from 'hooks/useModal'
import ModalReadyToCheckout from 'components/buffet/modal/ModalReadyToCheckout'
import { ModalEnum } from 'components/buffet/BuffetModals'
import useHandleBuffetOrder from 'hooks/buffet/useHandleBuffetOrder'
import TopLabel from 'components/buffet/TopLabel'
import SummaryOrder from 'components/SummaryOrder'
import useSummaryOrderPrivateCart from 'hooks/useSummaryOrderPrivateCart'
import { PrivateCartType, ProductCartType } from 'types/cart'
import ExVatInfo, { WrapperWithExVatInfo } from 'components/ExVatInfo'
import { sendAnalyticsDineInViewItem } from 'helpers/analytics'
import { generateBuffetProductDetailsUrl } from 'helpers/buffet/product'
import { vendorSelector } from 'store/slices/vendorSlice'

export interface Modifier {
  id: number
  nameEn: string
  nameTh: string
}

export interface ModifierGroup {
  id: number
  modifierGroup: {
    nameEn: string
    nameTh: string
    modifiers: Modifier[]
  }
}

export interface ProductType {
  id: number
  nameTh: string
  nameEn: string
  modifierGroups: ModifierGroup[]
}

export type PaymentStatusType = 'None' | 'Paid' | 'PendingPayment' | 'Failed'

const { primary } = colors
const warningImage = `${process.env.PUBLIC_URL}/assets/dine-in/warning.svg`

const PrivateCart = () => {
  const { brand } = useContext(AppContext)
  const buffet = useSelector(buffetSelector)
  const privateCart = useSelector(privateCartSelector)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const modalConfirmRemoveCartItem = useModal()
  const modalConfirmSubmitItems = useModal()
  const [indexItemForRemove, setIndexItemForRemove] = useState(0)
  const [lineItemsResult, setLineItemResult] = useState<any[]>([])
  const [isLoadingModalConfirm, setIsLoadingModalConfirm] = useState(true)
  const [titleError, setTitleError] = useState<string>(t('dineIn.modal.errorSubmitToKitchen.title'))
  const [isShowErrorFromSubmit, setIsShowErrorFromSubmit] = useState(false)
  const [isCanScroll, setIsCanScroll] = useState(false)
  const scrollPageRef = useRef<HTMLDivElement>(null)
  const modalReadyToCheckout = useModal()
  const { validateCurrentOrder } = useValidateCurrentOrder()
  const { handleValidateResult, handleBuffetEndTime } = useHandleBuffetOrder({
    skipModal: [ModalEnum.ReadyToCheckout],
  })
  const [productRemovingIndex, setProductRemovingIndex] = useState(-1)

  const requestOption = {
    client: dineInClient,
    fetchPolicy: 'no-cache' as WatchQueryFetchPolicy,
  }

  const {
    setting: { excludingVat },
  } = useSelector(vendorSelector)

  const customValidateOrder = () => {
    const validateResult = validateCurrentOrder(buffet.currentOrder, true)
    handleBuffetEndTime(buffet.currentOrder?.buffetEndTime)
    if (!validateResult.isOK) {
      if (validateResult.invalid === BuffetOrderInvalidEnum.ReadyToCheckout) {
        modalReadyToCheckout.setOpen(true)
        return false
      } else {
        if (validateResult.invalid === BuffetOrderInvalidEnum.PackageHasChanged) {
          dispatch(clearCurrentPrivateCart())
        }
        handleValidateResult(validateResult, buffet.currentOrder)
        return false
      }
    }
    return true
  }

  const handleBack = () => {
    navigate(SubPageName.Home)
  }

  useBuffetOrderPackageQuery({
    ...requestOption,
    skip: !buffet.table.tableID || !brand?.id,
    variables: {
      tableID: buffet.table.tableID,
      orderID: buffet.table.orderID,
      vendorID: Number(brand?.id),
    },
    onCompleted: (result) => {
      dispatch(onCurrentPackageChange(result.currentOrder.buffetPackage))
    },
  })

  const [submitOrderLineItemsMutation] = useSubmitOrderLineItemsMutation({ client: dineInClient })
  const onEditItem = (selectedIndex: number) => {
    if (privateCart.current) {
      const targetProduct = privateCart.current.products[selectedIndex]
      dispatch(openModalEditProductModifier({ skipCustomNotesScreen: targetProduct.skipCustomNotesScreen }))
      dispatch(setPrivateCartSelectedIndex(selectedIndex))
      dispatch(setProductSelected(targetProduct))
      dispatch(setLoadingModifier(false))
    }
  }

  const onUpdateItemQuantity = (quantity: number, selectedIndex: number) => {
    if (privateCart.current) {
      dispatch(setPrivateCartSelectedIndex(selectedIndex))
      dispatch(updatePrivateCartItem({ ...privateCart.current.products[selectedIndex], quantity }))
    }
  }

  const openModalConfirmRemoveCartItem = (selectedIndex: number) => {
    setIndexItemForRemove(selectedIndex)
    setTimeout(() => {
      setIsLoadingModalConfirm(false)
      modalConfirmRemoveCartItem.setOpen(true)
    }, 300)
  }

  const onRemovePrivateCartItem = (selectedIndex: number) => {
    modalConfirmRemoveCartItem.setOpen(false)
    setProductRemovingIndex(selectedIndex)
    setTimeout(() => {
      setProductRemovingIndex(-1)
      dispatch(removePrivateCartItem(selectedIndex))
    }, 300)
  }

  const openModalConfirmSubmitItems = () => {
    setTimeout(() => {
      setIsLoadingModalConfirm(false)
      modalConfirmSubmitItems.setOpen(true)
    })
  }

  const onSubmitItemsToGlobalCart = async () => {
    if (customValidateOrder()) {
      openModalConfirmSubmitItems()
    }
  }

  const convertProductsToLineItems = useCallback(() => {
    const mapProductPrivateCartToOrderLineItemInput = ({
      item,
      customNotes,
      quantity,
      modifiers,
      serviceType,
    }: ProductCartType): OrderLineItemInput => {
      return {
        productID: item.id,
        quantity: quantity,
        customNotes: customNotes,
        modifiers: modifiers.map(({ id, quantity }) => ({
          modifierID: id,
          quantity: quantity,
        })),
        serviceType: serviceType as OrderLineItemInput['serviceType'],
      }
    }

    return privateCart.current?.products.map(mapProductPrivateCartToOrderLineItemInput) || []
  }, [privateCart])

  const lineItems = useMemo(() => {
    return convertProductsToLineItems()
  }, [convertProductsToLineItems])

  const { summaryPrivateCart, isHavePromoDiscount } = useSummaryOrderPrivateCart({
    tableID: buffet.table.tableID,
    lineItems,
  })

  const onConfirmSubmitItems = async () => {
    modalConfirmSubmitItems.setProcessing(true)
    if (customValidateOrder()) {
      const lineItems: OrderLineItemInput[] = convertProductsToLineItems()
      const { data } = await submitOrderLineItemsMutation({
        variables: {
          orderID: buffet.table.orderID,
          lineItems,
        },
      })
      if (data) {
        const { submitOrderLineItems } = data
        const {
          orderInvalids,
          promotionInvalids,
          order: { buffetEndTime },
        } = submitOrderLineItems
        const isSuccess = orderInvalids.length === 0 && promotionInvalids.length === 0
        if (isSuccess || orderInvalids.includes(OrderInvalidEnum.InvalidLineItem)) {
          const _lineItemResult = submitOrderLineItems.order.lineItems
          const _hasInvalids = _lineItemResult.some(
            (lineItem) =>
              lineItem.invalids?.length || lineItem.modifiers.some((modifier) => modifier.modifier.invalids?.length)
          )
          setLineItemResult(_lineItemResult)

          if (_hasInvalids) {
            setIsShowErrorFromSubmit(true)
          } else {
            dispatch(clearCurrentPrivateCart())
            if (buffet.endTime === null || buffet.endTime !== buffetEndTime) {
              dispatch(setBuffetEndTime(buffetEndTime))
            }
            navigate(SubPageName.GlobalCart)
          }
        } else {
          const isBeingCheckout = orderInvalids.includes(OrderInvalidEnum.InvalidBeingCheckedOut)
          if (!isBeingCheckout) {
            const titleError = getOrderInvalidWarningText(orderInvalids)
            setTitleError(titleError ?? t('dineIn.modal.errorSubmitToKitchen.title'))
            setIsShowErrorFromSubmit(true)
          }
        }
      } else {
        setIsShowErrorFromSubmit(true)
      }
    }
    modalConfirmSubmitItems.reset()
  }

  const handleContentResize = _.debounce((contentWrapper: HTMLDivElement | null) => {
    if (contentWrapper) {
      const _isCanScroll = window.innerHeight < contentWrapper.offsetHeight
      setIsCanScroll(_isCanScroll)
    }
  }, 200)

  const { currentPrivateCartBuffet, currentPrivateCartBuffetTakeAway } = useMemo(() => {
    let currentPrivateCartBuffet: PrivateCartType | null = null
    let currentPrivateCartBuffetTakeAway: PrivateCartType | null = null
    if (privateCart?.current?.products) {
      const currentPrivateCart = privateCart.current
      currentPrivateCartBuffet = { orderId: currentPrivateCart.orderId, products: [] }
      currentPrivateCartBuffetTakeAway = { orderId: currentPrivateCart.orderId, products: [] }

      currentPrivateCart.products.forEach((product, privateCartIndex) => {
        if (product.serviceType === LineItemServiceEnum.BuffetALaCarteTakeaway) {
          currentPrivateCartBuffetTakeAway?.products.push({ ...product, privateCartIndex })
        } else {
          currentPrivateCartBuffet?.products.push({ ...product, privateCartIndex })
        }
      })
    }

    return {
      currentPrivateCartBuffet,
      currentPrivateCartBuffetTakeAway,
    }
  }, [privateCart])

  const handleClickPrivateCartItem = (product: ProductCartType) => {
    sendAnalyticsDineInViewItem({ product: product.item })
    const productDetailUrl = generateBuffetProductDetailsUrl({
      categoryID: product.item.categoryID,
      productID: product.item.id,
      serviceType: product.serviceType,
    })
    navigate(productDetailUrl)
  }

  const isHaveCurrentPrivateCartBuffet =
    currentPrivateCartBuffet && currentPrivateCartBuffet?.products?.length !== 0 ? true : false
  const isHaveCurrentPrivateCartBuffetTakeAway =
    currentPrivateCartBuffetTakeAway && currentPrivateCartBuffetTakeAway?.products?.length !== 0 ? true : false

  return (
    <BuffetContainer activeFooter>
      <ScrollPage id="private-cart-page" ref={scrollPageRef}>
        <ContentResize resizeCallback={handleContentResize} style={{ minHeight: '100%', flexShrink: 0 }}>
          <TopLabel endTime={buffet.endTime} />
          <Wrapper>
            {isHaveCurrentPrivateCartBuffet || isHaveCurrentPrivateCartBuffetTakeAway ? (
              <>
                <DetailsWrapper>
                  <PrivateCartSection>
                    <BtnBackStyled bold onClick={handleBack} enableFastClick={false} />
                    <LabelWithIcon
                      title={t('buffet.privateCart.header')}
                      size="lg"
                      bold
                      iconName="basket-3"
                      iconSize="20px"
                    />
                    {currentPrivateCartBuffet && isHaveCurrentPrivateCartBuffet && (
                      <PrivateCartItemList
                        currentPrivateCart={currentPrivateCartBuffet}
                        onUpdateItemQuantity={onUpdateItemQuantity}
                        onClickProduct={handleClickPrivateCartItem}
                        onClickEdit={onEditItem}
                        onClickDelete={openModalConfirmRemoveCartItem}
                        lineItemsResult={lineItemsResult}
                        hideZeroPrice
                        productRemovingIndex={productRemovingIndex}
                      />
                    )}
                    {currentPrivateCartBuffetTakeAway && isHaveCurrentPrivateCartBuffetTakeAway && (
                      <TakeAwayLineItemsContainer>
                        <PrivateCartItemList
                          currentPrivateCart={currentPrivateCartBuffetTakeAway}
                          onUpdateItemQuantity={onUpdateItemQuantity}
                          onClickProduct={handleClickPrivateCartItem}
                          onClickEdit={onEditItem}
                          onClickDelete={openModalConfirmRemoveCartItem}
                          lineItemsResult={lineItemsResult}
                          productRemovingIndex={productRemovingIndex}
                        />
                      </TakeAwayLineItemsContainer>
                    )}
                  </PrivateCartSection>
                  {isHavePromoDiscount && summaryPrivateCart && (
                    <SummaryWrapper>
                      <SummaryOrder
                        lineItemTotalSatangs={summaryPrivateCart.lineItemTotalSatangs}
                        adjustments={summaryPrivateCart.adjustments}
                        totalSatangs={summaryPrivateCart.totalSatangs}
                      />
                    </SummaryWrapper>
                  )}
                </DetailsWrapper>
                <WrapperStickyBottom isSticky={isCanScroll}>
                  <WrapperWithExVatInfo>
                    <ButtonSubmitOrder
                      id="sendToKitchenBtn"
                      data-testid="sendToKitchenBtn"
                      disabled={buffet.isTimeUp}
                      onClick={onSubmitItemsToGlobalCart}
                    >
                      {t('buffet.privateCart.submitOrder')}
                    </ButtonSubmitOrder>
                    {excludingVat && <ExVatInfo />}
                  </WrapperWithExVatInfo>
                  {isHavePromoDiscount && (
                    <LabelInfoWrapper>
                      <LabelInfo text={t('label.displayPromoDiscountOnlyInCartPage')} />
                    </LabelInfoWrapper>
                  )}
                </WrapperStickyBottom>
              </>
            ) : (
              <EmptyLineItems onClick={() => navigate(SubPageName.Home)} />
            )}

            <ModalConfirm
              testId="privateCartPageModalConfirm"
              isOpen={modalConfirmRemoveCartItem.open}
              onRequestClose={() => modalConfirmRemoveCartItem.setOpen(false)}
              shouldCloseOnOverlayClick={false}
              isLoading={isLoadingModalConfirm}
              hideCloseButton={false}
              btnAccept={{
                outline: false,
                content: t('btnOK'),
                onClick: () => onRemovePrivateCartItem(indexItemForRemove),
              }}
              btnCancel={{
                outline: true,
                content: t('btnCancel'),
                onClick: () => modalConfirmRemoveCartItem.setOpen(false),
              }}
            >
              <ModalContent>
                <Fonts.H3>{t('dineIn.modal.removeCartItem.title')}</Fonts.H3>
                <Fonts.Base style={{ textAlign: 'center' }}>{t('dineIn.modal.removeCartItem.description')}</Fonts.Base>
              </ModalContent>
            </ModalConfirm>

            <ModalConfirmSubmitLineItem
              isOpen={modalConfirmSubmitItems.open}
              isLoading={isLoadingModalConfirm}
              onRequestClose={() => modalConfirmSubmitItems.setOpen(false)}
              isSubmitting={modalConfirmSubmitItems.processing}
              onAccept={onConfirmSubmitItems}
              onCancel={() => modalConfirmSubmitItems.setOpen(false)}
              duplicateLineItems={[]}
              options={{
                title: t('buffet.modal.submitOrderToKitchen.title'),
                description: t('buffet.modal.submitOrderToKitchen.description'),
                confirm: t('buffet.modal.submitOrderToKitchen.confirm'),
                cancel: t('buffet.modal.submitOrderToKitchen.cancel'),
              }}
            />

            <Modal
              isOpen={isShowErrorFromSubmit}
              shouldCloseOnOverlayClick={false}
              onRequestClose={() => setIsShowErrorFromSubmit(false)}
              btnClose={t('btnOK')}
              hideCloseButton={false}
            >
              <ModalContent>
                <img
                  width={45}
                  alt="generic-icon-warning"
                  src={warningImage}
                  srcSet={generateSrcSet(warningImage, 'png')}
                />
                <Fonts.H2 style={{ marginTop: '15px' }}>{titleError}</Fonts.H2>
                <Fonts.Base>{t('dineIn.modal.errorSubmitToKitchen.description')}</Fonts.Base>
              </ModalContent>
            </Modal>
            <ModalReadyToCheckout
              isOpen={modalReadyToCheckout.open}
              title={t('buffet.modal.orderHasConfirmCheckout.title')}
              description={t('buffet.modal.orderHasConfirmCheckout.description')}
              onClick={() => {
                modalReadyToCheckout.setOpen(false)
                navigate(SubPageName.GlobalCart)
              }}
            />
          </Wrapper>
        </ContentResize>
      </ScrollPage>
    </BuffetContainer>
  )
}

export default PrivateCart

const Wrapper = styled.div`
  padding: 15px;
  flex: 1;
  display: flex;
  flex-direction: column;
`

const DetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${({ theme }) => theme.spacing['3XL']};
  padding-bottom: ${({ theme }) => theme.spacing['3XL']};
`

const ButtonSubmitOrder = styled(Button)`
  max-width: 290px;
  min-height: 50px;
  border-radius: 85px;
  align-self: center;

  background-color: ${primary};
  border: 1px solid ${primary};

  :disabled {
    color: ${({ theme }) => theme.colors.white}!important;
    border: 1px solid ${({ theme }) => theme.colors.gray300};
    background: ${({ theme }) => theme.colors.gray300}!important;
  }

  .no-touch &:hover,
  .touch &:active {
    background-color: ${({ theme }) => theme.colors.white}!important;
    color: ${primary};
  }
`

const BtnBackStyled = styled(BtnBack)`
  i {
    font-size: ${({ theme }) => theme.fontSize.xxl};
  }
`

const PrivateCartSection = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${({ theme }) => theme.spacing['2XL']};
  padding-bottom: 30px;
`

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 7px;
  padding-bottom: ${({ theme }) => theme.spacing['2XL']};

  .icon-idd-warning-triangle-redius {
    color: #ec7100 !important;
  }
`

const SummaryWrapper = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing['6XL']};
`
const LabelInfoWrapper = styled.div`
  margin-top: ${({ theme }) => theme.spacing['2XL']};
`

const WrapperStickyBottom = styled.div<{ isSticky: boolean }>`
  position: sticky;
  bottom: ${({ theme }) => theme.spacing['8XL']};
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: white;
  padding-bottom: 41px;
  padding-top: ${({ theme }) => theme.spacing['2XL']};
  margin-top: auto;
  margin-left: -15px;
  margin-right: -15px;

  ${({ isSticky }) =>
    isSticky
      ? `
    box-shadow: 0px 0px 10px #e0e0e0;
  `
      : ''}
`
